import { useEffect, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getNavItems, LANGUAGE_PATH } from "./NavHelpers";
import {
  CapabilityNav,
  PageHeader,
  confirmDialog,
} from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import UsersService from "@velocity-ehs/user-utils-module";
import EhsConfigService from "../../../services/EhsConfigService";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { EhsConfiguration } from "./Utils";
import useEhsConfigurationMutations, {
  EhsConfigurationActions,
} from "../../../hooks/useEhsConfigurationMutations";
import { UploadConfigFileSlider } from "./UploadConfigFileSlider";
import { UpdateConfigFileSlider } from "./UpdateConfigFileSlider";
import CommonConfigTable from "./CommonConfigTable";
import LanguageConfigTable from "./LanguagesConfigTable";
import { useGetEhsConfigurationTypes } from "../../../hooks/useGetEhsConfigurationTypes";

const FILE_TYPE_EXTENSIONS = {
  "application/json": ".json",
  "application/xml": ".xml",
};

const EhsConfigurations = () => {
  const allFlags = useFlags();
  const { t } = useTranslation();
  const { config } = useParams<{ config: string }>();
  const { showSnackbar } = useSnackbarActionContext();
  const navItems = getNavItems(UsersService.permissions, allFlags);
  const current = navItems.find((item) => item.id === config);
  const [configTypeId, setConfigTypeId] = useState<string | undefined>();
  const [isUploadSliderOpen, setIsUploadSliderOpen] = useState(false);
  const [isUpdateSliderOpen, setIsUpdateSliderOpen] = useState(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState<{
    id: string;
    description: string;
    customerId: string;
  } | null>(null);
  const history = useHistory();
  const { ehsConfigurationTypes, languageConfigurationTypes } =
    useGetEhsConfigurationTypes();
  const { mutations } = useEhsConfigurationMutations();

  useEffect(() => {
    //if you navigate to a config type that is feature flagged or does not exist, redirect to the first valid config instead
    if (!current) {
      history.push(navItems[0]?.path);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [current]);

  useEffect(() => {
    const type = ehsConfigurationTypes.find((type) => type.name === current.id);
    if (type) {
      setConfigTypeId(type.id);
    }
  }, [ehsConfigurationTypes, current, setConfigTypeId]);

  const handleSelection = (event: {
    data: { id: string; description: string; customerId: string };
  }) => {
    setSelectedConfiguration(event.data);
    setIsUpdateSliderOpen(true);
  };

  const createFileAndDownload = async (blob, rowData) => {
    const ext = FILE_TYPE_EXTENSIONS[blob.type] || ".xml";

    // Create a link element
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob); // Create a URL object
    link.download = rowData.description
      ? `${rowData.description}${ext}`
      : `${rowData.configurationType.name}_ver-${rowData.version}${ext}`;

    // Append the link to the body, click it, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href); // Clean up the URL object
  };

  const handleConfiguFileDownload = async (rowData) => {
    try {
      await EhsConfigService.downloadConfigFile(
        UsersService.customer.id,
        rowData.id
      ).then((configFileData) => {
        createFileAndDownload(configFileData, rowData);
      });
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    }
  };

  const confirmActivateEhsConfiguration = (rowData: EhsConfiguration) => {
    confirmDialog({
      header: t("activate_ehs_configuration_modal_header"),
      message: t("activate_ehs_configuration_modal_message"),
      accept: () => mutations.mutate(EhsConfigurationActions.ACTIVATE, rowData),
      reject: () => {},
      acceptLabel: t("universal_Activate"),
      rejectLabel: t("universal_Cancel"),
    });
  };

  const rowActions = (rowData: EhsConfiguration) => {
    return [
      {
        command: () => handleConfiguFileDownload(rowData),
        label: t("universal_Download"),
      },
      {
        command: () => confirmActivateEhsConfiguration(rowData),
        label: t("universal_Activate"),
        visible: !rowData.active,
      },
    ];
  };

  const tableMap = {
    [LANGUAGE_PATH]: LanguageConfigTable,
  };

  const DefaultTable = CommonConfigTable;

  const TableDisplay = ({ id }) => {
    const TableComponent = tableMap[id] || DefaultTable;
    return (
      <TableComponent
        configType={configTypeId}
        languageConfigTypes={languageConfigurationTypes}
        rowActions={rowActions}
        setSelected={handleSelection}
      />
    );
  };

  return (
    <div className="App">
      <CapabilityNav
        capability="EHS Configurations"
        navItems={navItems}
        featureFlags={allFlags}
      />
      <div className="mainContent">
        <PageHeader
          title={current?.label || ""}
          navigationTree={[
            { text: `Accelerate ${t("universal_Home")}`, href: "/" },
            { text: `EHS ${t("ehs_configurations")}`, href: "" },
          ]}
          primaryButton={{
            label: t("universal_Upload"),
            id: "upload-config-file",
            onClick: () => setIsUploadSliderOpen(true),
          }}
        />
        <div className="card__fullContent">
          <TableDisplay id={current?.id} />
          <UploadConfigFileSlider
            showSlider={isUploadSliderOpen}
            currentId={current?.id}
            configTypes={ehsConfigurationTypes}
            languageConfigTypes={languageConfigurationTypes}
            closeSliderCallback={() => setIsUploadSliderOpen(false)}
          ></UploadConfigFileSlider>
          <UpdateConfigFileSlider
            showSlider={isUpdateSliderOpen}
            selectedConfig={selectedConfiguration}
            closeSliderCallback={() => setIsUpdateSliderOpen(false)}
          ></UpdateConfigFileSlider>
        </div>
      </div>
    </div>
  );
};

export default EhsConfigurations;
